/* eslint-disable no-restricted-imports */
import React, {useCallback, useEffect, useState} from 'react'
import {UIGameActionButton} from '../../../common/UIGameActionButton'
import {T} from '../../../common/T'
import {ACTION_BUTTON_IDS, useActionButtonCommonErrors} from '../utils'
import IconHourGlass from '@static/images/icon_hourglass.svg'
import IconPassTurn from '@static/images/icon_pass_turn.svg'
import {useHotkeys} from 'react-hotkeys-hook'
import {useGameStore} from '../../../store/GameStoreContext'
import clsx from 'clsx'
import styles from '../UIGameActionButtonContainer.module.scss'
import {HiddenUIElement} from '@colonist/game-beginner-mode'
// eslint-disable-next-line no-restricted-imports
import {StorageWrapper} from '../../../../../../code/client/shared/scripts/StorageWrapper'
import {useUIGameSocketContext} from '../../../contexts/UIGameSocketContext'
import {useEndTurnSuggestion} from './useEndTurnSuggestion'
import {GameAction} from '@colonist/game-base-types'
import {useCurrentStateValidator} from '../../../store/selectors/useCurrentStateValidator'

const SPACEBAR_SHORTCUT_DISABLED_AT_TURN_START_MS = 500

export const UIGameActionButtonTurn = ({
    className,
    hideTooltip = false,
}: {
    className?: string
    hideTooltip?: boolean
}) => {
    const [diceRolledTime, setDiceRolledTime] = useState(0)
    const {sendClickedPassTurn} = useUIGameSocketContext()
    const beginnerActionState = useGameStore((state) => state.gameBeginnerHint.beginnerActionState)
    const diceState = useGameStore((state) => state.gameState.diceState)
    const myColor = useGameStore((state) => state.myColor)
    const {errorHelperText, hasCommonErrors} = useActionButtonCommonErrors()
    const isHidden = beginnerActionState?.hiddenUIElements.includes(HiddenUIElement.EndTurnActionButton)
    const isNotBeginnerPassedTurnAction = beginnerActionState != null && beginnerActionState?.type !== GameAction.PassedTurn
    const currentStateValidator = useCurrentStateValidator()
    const hasPlayerToggledEndTurn = currentStateValidator.hasPlayerToggledEndTurn(myColor)
    const disabled = (hasCommonErrors || isHidden || isNotBeginnerPassedTurnAction) && !hasPlayerToggledEndTurn
    const {shouldShowSuggestion} = useEndTurnSuggestion()
    const setIsShowTradePanel = useGameStore((state) => state.gameTrade.setIsShowTradePanel)

    const onClick = useCallback(() => {
        if(disabled) return
        sendClickedPassTurn()
        setIsShowTradePanel(false)
    }, [disabled, sendClickedPassTurn])

    useEffect(() => {
        if(diceState.diceThrown) {
            setDiceRolledTime(Date.now())
        }
    }, [diceState.diceThrown])

    const isListeningForKeyPress = useCallback(() => {
        if(!StorageWrapper.getKeyboardShortcuts()) return false
        if(disabled) return false

        const msSinceDiceRolled = Date.now() - diceRolledTime
        if(msSinceDiceRolled < SPACEBAR_SHORTCUT_DISABLED_AT_TURN_START_MS) return false
        return true
    }, [disabled, diceRolledTime])

    useHotkeys('Space', () => {
        if(isListeningForKeyPress()) onClick?.()
    }, [isListeningForKeyPress])

    const icon = disabled ? IconHourGlass : IconPassTurn

    return <UIGameActionButton
        className={clsx(className, {
            [styles.hidden]: isHidden,
        })}
        id={ACTION_BUTTON_IDS.passTurn}
        onClick={onClick}
        disabled={disabled}
        helperText={disabled ? errorHelperText : undefined}
        iconPath={icon}
        highlight={hasPlayerToggledEndTurn}
        tooltipTitle={hideTooltip ? undefined : <T id='strings:game.tooltips.endTurn'/>}
        scalePulseAnimation={shouldShowSuggestion}
    />
}
